import { useEffect, useMemo, useRef, useState } from 'react'
import type { LibraryKey, Tag, TagGroup, TagGroupDef } from '@shared/types'
import { libraryDef } from '@shared/data/libraries'
import { addTagToAssets, createTag, getAssetRepository, removeTagFromAssets } from '@shared/services'

interface Props {
  assetIds: string[]
  onDone: (message: string, failed?: boolean) => void
  onClose: () => void
  /**
   * Whose tags: the library whose page this is. Tagging with a library's tag
   * files the picture into it, so the Audit list on the Design Works page
   * would quietly put Design Works pictures into Audit.
   */
  library?: LibraryKey
}

/**
 * Tagging a whole selection at once.
 *
 * This is deliberately not an image detail view. Everything needed to say
 * "these pictures are squeeze bottles" already exists — the grid, sweep
 * selection, the tray — so the missing piece was one control, not a new
 * surface. Correcting a single picture is the same gesture with one selected.
 */
export function BulkTagBar({ assetIds, onDone, onClose, library = 'audit' }: Props) {
  const def = libraryDef(library)
  const repository = useMemo(() => getAssetRepository(), [])
  const [tags, setTags] = useState<Tag[]>([])
  const [groups, setGroups] = useState<TagGroupDef[]>([])
  const [text, setText] = useState('')
  const [group, setGroup] = useState<TagGroup>('other')
  const [busy, setBusy] = useState(false)
  /**
   * The tags the selection already carries (Huanrui, 2026-10-27): how many of
   * the selected pictures carry each, read fresh from the library -- the
   * pictures in the selection were found by a search, which does not say.
   * Null while it is being read.
   */
  const [carried, setCarried] = useState<Map<string, number> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let live = true
    setCarried(null)
    void (async () => {
      const counts = new Map<string, number>()
      // The library answers a thousand at a time.
      for (let i = 0; i < assetIds.length; i += 1000) {
        const assets = await repository.getByIds(assetIds.slice(i, i + 1000))
        for (const asset of assets) {
          for (const tagId of asset.tagIds) counts.set(tagId, (counts.get(tagId) ?? 0) + 1)
        }
      }
      if (live) setCarried(counts)
    })().catch(() => {
      if (live) setCarried(new Map())
    })
    return () => {
      live = false
    }
  }, [repository, assetIds])

  useEffect(() => {
    let live = true
    void Promise.all([repository.listTags(library), repository.listTagGroups(library)])
      .then(([tagList, groupList]) => {
        if (!live) return
        setTags(tagList)
        setGroups(groupList)
        // Whatever the filter calls its catch-all, or failing that the first
        // category there is — never a name written into this file.
        const fallback =
          groupList.find((group) => group.slug === def.defaultGroup) ??
          groupList.find((group) => !group.hidden)
        if (fallback) setGroup(fallback.slug)
      })
      .catch(() => undefined)
    inputRef.current?.focus()
    return () => {
      live = false
    }
  }, [repository, library, def.defaultGroup])

  // Typed names are matched without regard to case or stray spaces, because
  // the list is a suggestion and people type past it.
  const needle = text.trim().toLowerCase()
  const match = tags.find((tag) => tag.name.toLowerCase() === needle) ?? null
  const isNew = needle.length > 0 && !match

  /** What the selection carries, as it is now after adding or taking away a tag. */
  const setCount = (tagId: string, n: number) =>
    setCarried((now) => {
      if (!now) return now
      const next = new Map(now)
      if (n > 0) next.set(tagId, n)
      else next.delete(tagId)
      return next
    })

  async function run(work: () => Promise<string>, keepText = false) {
    setBusy(true)
    try {
      onDone(await work())
      if (!keepText) setText('')
    } catch (error) {
      onDone(error instanceof Error ? error.message : 'That did not work.', true)
    } finally {
      setBusy(false)
    }
  }

  const count = assetIds.length
  const images = `${count} image${count === 1 ? '' : 's'}`

  // This library's tags on the selection: those on every picture first, then
  // by how many carry them. Tags of hidden groups (the packaging buttons'
  // tag) are not shown: they are set elsewhere.
  const hiddenGroups = new Set(groups.filter((one) => one.hidden).map((one) => one.slug))
  const onSelection = carried
    ? tags
        .filter((tag) => (carried.get(tag.id) ?? 0) > 0 && !hiddenGroups.has(tag.group))
        .map((tag) => ({ tag, n: carried.get(tag.id) ?? 0 }))
        .sort((a, b) => b.n - a.n || a.tag.name.localeCompare(b.tag.name))
    : []

  return (
    <div className="web-tray__tagbar">
      <input
        ref={inputRef}
        className="web-tray__taginput"
        list="ww-bulk-tags"
        value={text}
        placeholder="Tag name"
        spellCheck={false}
        disabled={busy}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose()
        }}
      />
      <datalist id="ww-bulk-tags">
        {tags.map((tag) => (
          <option key={tag.id} value={tag.name} />
        ))}
      </datalist>

      {/* Only asked for when the tag does not exist yet: a new tag has to be
          filed somewhere, and `other` is a place to put things, not an answer. */}
      {isNew && def.hasCategories ? (
        <select
          className="web-tray__tagselect"
          value={group}
          disabled={busy}
          onChange={(event) => setGroup(event.target.value)}
        >
          {groups.map((option) => (
            <option key={option.slug} value={option.slug}>
              {option.label}
            </option>
          ))}
        </select>
      ) : null}

      <button
        type="button"
        className="ww-btn ww-btn--primary ww-btn--sm"
        disabled={busy || needle.length === 0}
        onClick={() =>
          void run(async () => {
            const tag = match ?? (await createTag(text.trim(), group, library))
            await addTagToAssets(tag.id, assetIds)
            if (!match) setTags((now) => [...now, tag])
            setCount(tag.id, count)
            return match
              ? `${tag.name} added to ${images}.`
              : `Created ${tag.name} in ${groups.find((g) => g.slug === group)?.label ?? group} and added it to ${images}.`
          })
        }
      >
        {isNew ? `Create & add to ${count}` : `Add to ${count}`}
      </button>

      {/* Removing something that was never there is not an error, so this only
          appears for a tag that exists. */}
      <button
        type="button"
        className="ww-btn ww-btn--ghost ww-btn--sm"
        disabled={busy || !match}
        onClick={() =>
          void run(async () => {
            await removeTagFromAssets(match!.id, assetIds)
            setCount(match!.id, 0)
            return `${match!.name} removed from ${images}.`
          })
        }
      >
        Remove
      </button>

      <button type="button" className="ww-btn ww-btn--ghost ww-btn--sm" onClick={onClose}>
        Done
      </button>

      {/* What the selection already carries; × takes a tag off every selected picture that has it. */}
      <div className="web-tray__tagson">
        {carried === null ? (
          <span className="web-tray__tagsnote">Reading their tags…</span>
        ) : onSelection.length === 0 ? (
          <span className="web-tray__tagsnote">No tags on these yet</span>
        ) : (
          onSelection.map(({ tag, n }) => (
            <span
              key={tag.id}
              className={`web-tray__tagchip${n < count ? ' web-tray__tagchip--some' : ''}`}
              title={n < count ? `On ${n} of the ${count} selected` : `On all ${count} selected`}
            >
              {tag.name}
              {n < count ? <span className="web-tray__tagchipn">{n}/{count}</span> : null}
              <button
                type="button"
                className="web-tray__tagchipx"
                disabled={busy}
                aria-label={`Remove ${tag.name}`}
                onClick={() =>
                  void run(async () => {
                    await removeTagFromAssets(tag.id, assetIds)
                    setCount(tag.id, 0)
                    return `${tag.name} removed from ${n} image${n === 1 ? '' : 's'}.`
                  }, true)
                }
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>
    </div>
  )
}
